import { VisitorType as AmbassadorVisitorType, } from '@wix/ambassador-events-v1-event/types';
import { EventStatus as AmbassadorEventStatus, EventType as AmbassadorEventType, LocationType as AmbassadorLocationType, RegistrationStatus as AmbassadorRegistrationStatus, RsvpStatusOptions as AmbassadorRsvpStatusOptions, Status as AmbassadorStatus, TaxType as AmbassadorTaxType, } from '@wix/ambassador-wix-events-web/types';
import { EventStatus as EventStatusNumberEnum, EventType, LocationType, RegistrationStatus, RsvpStatusOptions, Status, TaxType, VisitorType, } from '@wix/events-types';
import { defaultTimeZoneId } from '../locale';
import { getFormattedMoney } from '../price';
export const getEventId = (event) => event.id;
export const getSlug = (event) => event.slug;
export const getEventTitle = (event) => event.title;
export const getEventTicketLimitPerOrder = (event) => event.registration.ticketing?.config?.ticketLimitPerOrder;
export const getEventMainImage = (event) => event.mainImage;
export const getEventDescription = (event) => event.description;
export const getRegistrationStatus = (event) => event.registration.status;
export const getRegistrationStartDate = (event) => {
    const registrationStartDate = event.registration.rsvpCollection?.config.startDate;
    if (typeof registrationStartDate === 'string') {
        return registrationStartDate;
    }
    return registrationStartDate?.toISOString();
};
export const getRegistrationEndDate = (event) => {
    const registrationEndDate = event.registration.rsvpCollection?.config.endDate;
    if (typeof registrationEndDate === 'string') {
        return registrationEndDate;
    }
    return registrationEndDate?.toISOString();
};
export const getAboutText = (event) => (event.about && event.about !== '<p></p>' ? event.about : '');
export const isEndDateHidden = (event) => event.scheduling.config.endDateHidden;
export const isTicketed = (event) => event.registration.type === EventType.TICKETS || event.registration.type === AmbassadorEventType.TICKETS;
export const isInitiallyTicketed = (event) => event.registration.initialType === EventType.TICKETS || event.registration.initialType === AmbassadorEventType.TICKETS;
export const ticketedOrInitiallyTicketed = (event) => isTicketed(event) || isInitiallyTicketed(event);
export const isInitiallyRsvp = (event) => event.registration.initialType === EventType.RSVP || event.registration.initialType === AmbassadorEventType.RSVP;
export const isRsvp = (event) => event.registration.type === EventType.RSVP || event.registration.type === AmbassadorEventType.RSVP;
export const rsvpOrInitiallyRsvp = (event) => isRsvp(event) || isInitiallyRsvp(event);
export const isRegistrationClosed = (event) => isRegistrationAutomaticallyClosed(event) || isRegistrationManuallyClosed(event);
export const isScheduled = (event) => event.status === EventStatusNumberEnum.SCHEDULED || event.status === AmbassadorEventStatus.SCHEDULED;
export const isStarted = (event) => event.status === EventStatusNumberEnum.STARTED || event.status === AmbassadorEventStatus.STARTED;
export const isEnded = (event) => event.status === EventStatusNumberEnum.ENDED || event.status === AmbassadorEventStatus.ENDED;
export const isCanceled = (event) => event.status === EventStatusNumberEnum.CANCELED || event.status === AmbassadorEventStatus.CANCELED;
export const isDraft = (event) => event.status === EventStatusNumberEnum.DRAFT || event.status === AmbassadorEventStatus.DRAFT;
export const isPastAndDraft = (event) => isDraft(event) && new Date(getEndDate(event)) < new Date();
export const isPastOrCanceled = (event) => isEnded(event) || isCanceled(event);
export const isActive = (event) => !isEnded(event) && !isCanceled(event);
export const isRegistrationAutomaticallyClosed = (event) => event.registration.status === RegistrationStatus.CLOSED ||
    event.registration.status === AmbassadorRegistrationStatus.CLOSED;
export const isRegistrationManuallyClosed = (event) => (event.registration.status === RegistrationStatus.CLOSED_MANUALLY ||
    event.registration.status === AmbassadorRegistrationStatus.CLOSED_MANUALLY) &&
    !isExternal(event);
export const isRegistrationExternal = (event) => event.registration.status === RegistrationStatus.OPEN_EXTERNAL ||
    event.registration.status === AmbassadorRegistrationStatus.OPEN_EXTERNAL;
export const isRegistrationScheduled = (event) => event.registration.status === RegistrationStatus.SCHEDULED_RSVP ||
    event.registration.status === AmbassadorRegistrationStatus.SCHEDULED_RSVP;
export const isWithoutRegistration = (event) => event.registration.type === EventType.NO_REGISTRATION ||
    event.registration.type === AmbassadorEventType.NO_REGISTRATION;
export const isRegistrationWithCustomDates = (event) => Boolean(getRegistrationStartDate(event) && getRegistrationEndDate(event));
export const isExternal = (event) => event.registration.type === EventType.EXTERNAL || event.registration.type === AmbassadorEventType.EXTERNAL;
export const isInternal = (event) => isRsvp(event) || isTicketed(event) || (!event.id && !isExternal(event));
export const isWaitlistOpen = (event) => event.registration.status === RegistrationStatus.OPEN_RSVP_WAITLIST ||
    event.registration.status === AmbassadorRegistrationStatus.OPEN_RSVP_WAITLIST;
export const isRsvpOpen = (event) => event.registration.status === RegistrationStatus.OPEN_RSVP ||
    event.registration.status === AmbassadorRegistrationStatus.OPEN_RSVP;
export const isWaitlistEnabled = (event) => event.registration.rsvpCollection?.config.waitlist;
export const isDateTbd = (event) => Boolean(event.scheduling.config.scheduleTbd);
export const isEmpty = (event) => ticketedOrInitiallyTicketed(event) ? !hasTickets(event) : !hasRSVPs(event);
export const isFull = (event) => isScheduled(event) && (isRegistrationAutomaticallyClosed(event) || isWaitlistOpen(event));
export const isSoldOut = (event) => isTicketed(event) && event.registration.ticketing?.soldOut;
export const isPaid = (event) => Boolean(event.registration.ticketing.highestPrice);
export const isFree = (event) => !isPaid(event);
export const isWithDefaultForm = (event) => event.form.controls.length === 2;
export const isLocationTbd = (event) => !isEventLocationOnline(event) && !(event.location.address || event.location.fullAddress?.formattedAddress);
export const isEventLocationOnline = (event) => (event.location.type ?? LocationType.VENUE) === LocationType.ONLINE ||
    (event.location.type ?? AmbassadorLocationType.VENUE) === AmbassadorLocationType.ONLINE;
export const getFormattedLocation = (event) => event.location.name || event.location.address || event.location.fullAddress?.formattedAddress;
export const getFormattedFullLocation = (event) => {
    if (event.location.address && event.location.name) {
        return `${event.location.name}, ${event.location.address}`;
    }
    if (event.location.fullAddress?.formattedAddress && event.location.name) {
        return `${event.location.name}, ${event.location.fullAddress.formattedAddress}`;
    }
    return getFormattedLocation(event);
};
export const getCoordinates = (event) => event.location.coordinates;
export const getOrdersCount = ({ dashboard }) => dashboard?.ticketingSummary?.orders ?? 0;
export const getRSVPCount = ({ dashboard }) => dashboard?.rsvpSummary?.total ?? 0;
export const getGoingAttendeeCount = ({ dashboard }) => dashboard?.rsvpSummary?.yes ?? 0;
export const getNotGoingAttendeeCount = ({ dashboard }) => dashboard?.rsvpSummary?.no ?? 0;
export const hasRSVPs = (event) => !!getRSVPCount(event);
export const getTicketsCount = (event) => event?.dashboard?.ticketingSummary?.tickets ?? 0;
export const hasTickets = (event) => !!getTicketsCount(event);
export const hasOrders = (event) => Boolean(getOrdersCount(event));
export const hasOrdersOrRsvps = (event) => hasOrders(event) || hasRSVPs(event);
export const hasGuestsOrRsvps = (event) => !!getTotalGuests(event) || hasRSVPs(event);
export const getTotalGuests = (event) => {
    const ticketed = ticketedOrInitiallyTicketed(event);
    const initiallyRsvpOrTicketed = isInitiallyTicketed(event) || isInitiallyRsvp(event);
    const internal = isInternal(event);
    if (!internal && !initiallyRsvpOrTicketed) {
        return 0;
    }
    return ticketed ? getTicketsCount(event) : getRSVPCount(event);
};
export const hasImage = (event) => !!event.mainImage?.id;
export const getRestrictedTo = (event) => {
    const restrictedTo = event.registration.restrictedTo;
    if (typeof event.status === 'number') {
        return restrictedTo ?? VisitorType.VISITOR;
    }
    return restrictedTo ?? AmbassadorVisitorType.VISITOR;
};
export const getExternalLink = (event) => event.registration.external?.registration ?? null;
export const getGuestLimit = (event) => event.registration.rsvpCollection?.config.limit ?? null;
export const getRsvpStatus = (event) => event.registration.rsvpCollection?.config.rsvpStatusOptions;
export const getCurrency = (event) => event.registration?.ticketing?.currency;
export const isCurrencySet = (event) => !!getCurrency(event);
export const isCurrencyLocked = (event) => event.dashboard?.ticketingSummary.currencyLocked ?? true;
export const isAssignedTickets = (event) => !!event.registration?.ticketing?.config?.guestAssignedTickets;
export const getTaxConfig = (event) => event.registration?.ticketing?.config?.taxConfig ?? null;
export const isTaxIncluded = (event) => event.registration?.ticketing?.config?.taxConfig.type === TaxType.INCLUDED ||
    event.registration?.ticketing?.config?.taxConfig.type === AmbassadorTaxType.INCLUDED;
export const isNoResponseEnabled = (event) => event.registration.rsvpCollection?.config.rsvpStatusOptions === RsvpStatusOptions.YES_AND_NO ||
    event.registration.rsvpCollection?.config.rsvpStatusOptions === AmbassadorRsvpStatusOptions.YES_AND_NO;
export const getRsvpStatusOptions = (event) => event.registration.rsvpCollection?.config.rsvpStatusOptions;
export const getFormControls = (event) => event.form.controls;
export const getFormMessages = (event) => event.form.messages;
export const isOnlineConferencingEnabled = (event) => event.onlineConferencing?.config?.enabled ?? false;
export const getOnlineConferencingProviderId = (event) => event.onlineConferencing?.config?.providerId ?? '';
export const getOnlineConferencingType = (event) => event.onlineConferencing?.config?.conferenceType;
export const getOnlineConferencingGuestUrl = (event) => event.onlineConferencing?.session?.guestLink ?? '';
export const getOnlineConferencingHostUrl = (event) => event.onlineConferencing?.session?.hostLink ?? '';
export const getOnlineConferencingPassword = (event) => event.onlineConferencing?.session?.password ?? '';
export const getOnlineConferencingSessionId = (event) => event.onlineConferencing?.session?.sessionId ?? '';
export const getEventPageUrl = (event) => {
    const { base, path } = event.eventPageUrl;
    return `${base}${path.split('?')[0]}`;
};
export const getEventTimeZoneId = (event) => (!isDateTbd(event) && event.scheduling.config.timeZoneId) || defaultTimeZoneId;
/** @deprecated due to confusing naming, use getEventTimeZoneId */
export const getTimeZoneId = getEventTimeZoneId;
export const getShowTimeZone = (event) => event.scheduling.config.showTimeZone;
export const getStartDate = (event) => {
    const startDate = event.scheduling.config.startDate;
    if (typeof startDate === 'string') {
        return startDate;
    }
    return startDate?.toISOString();
};
export const getEndDate = (event) => {
    const endDate = event.scheduling.config.endDate;
    if (typeof endDate === 'string') {
        return endDate;
    }
    return endDate?.toISOString();
};
export const getOccurrences = (event) => event.scheduling.config.recurrences?.occurrences ?? [];
export const hasOccurrences = (event) => Boolean(getOccurrences(event).length);
export const isOneTimeEvent = (event) => getRecurrenceStatus(event) === Status.ONE_TIME || getRecurrenceStatus(event) === AmbassadorStatus.ONE_TIME;
export const isRecurringEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING ||
    getRecurrenceStatus(event) === AmbassadorStatus.RECURRING ||
    isRecurringNextEvent(event) ||
    isRecurringLastEndedEvent(event) ||
    isRecurringLastCanceledEvent(event);
export const isRecurringNextEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING_NEXT || getRecurrenceStatus(event) === AmbassadorStatus.RECURRING_NEXT;
export const isRecurringLastEndedEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING_LAST_ENDED ||
    getRecurrenceStatus(event) === AmbassadorStatus.RECURRING_LAST_ENDED;
export const isRecurringLastCanceledEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING_LAST_CANCELED ||
    getRecurrenceStatus(event) === AmbassadorStatus.RECURRING_LAST_CANCELED;
export const getRecurrenceStatus = (event) => event.scheduling.config.recurrences?.status ?? Status.ONE_TIME;
export const getRecurrenceCategoryId = (event) => event.scheduling.config.recurrences?.categoryId;
export const getTicketsPriceRange = (event) => {
    if (isTicketed(event) && hasVisibleTicketDefinitions(event)) {
        const { lowestTicketPriceFormatted, highestTicketPriceFormatted } = event.registration.ticketing;
        if (lowestTicketPriceFormatted === highestTicketPriceFormatted) {
            return lowestTicketPriceFormatted;
        }
        return `${lowestTicketPriceFormatted} - ${highestTicketPriceFormatted}`;
    }
    return undefined;
};
/** does not include manually hidden definitions */
export const hasVisibleTicketDefinitions = (event) => event.registration.ticketing?.lowestPrice !== undefined;
/** @deprecated due to confusing naming, use has hasVisibleTicketDefinitions */
export const hasTicketDefinitions = hasVisibleTicketDefinitions;
export const wasTicketed = (event) => {
    const eventRegistrationType = event.registration.type;
    const hasVisibleTicketDefinitionsOrTickets = hasVisibleTicketDefinitions(event) || hasTickets(event);
    if (typeof eventRegistrationType === 'number') {
        return eventRegistrationType !== EventType.RSVP && hasVisibleTicketDefinitionsOrTickets;
    }
    return eventRegistrationType !== AmbassadorEventType.RSVP && hasVisibleTicketDefinitionsOrTickets;
};
export const wasRsvp = (event) => {
    const eventRegistrationType = event.registration.type;
    const eventHasRsvps = hasRSVPs(event);
    if (typeof eventRegistrationType === 'number') {
        return eventRegistrationType !== EventType.TICKETS && eventHasRsvps;
    }
    return eventRegistrationType !== AmbassadorEventType.TICKETS && eventHasRsvps;
};
export const getTotalSales = (event, fallbackCurrency) => ticketedOrInitiallyTicketed(event) &&
    (event.dashboard.ticketingSummary.totalSales
        ? getFormattedMoney(event.dashboard.ticketingSummary.totalSales)
        : getFormattedMoney({ amount: '0', currency: getCurrency(event) || fallbackCurrency }));
export const getWaitlistedAttendees = (event) => event.dashboard?.rsvpSummary?.waitlist ?? 0;
export const hasOnlineConferencingProvider = (event) => Boolean(getOnlineConferencingProviderId(event));
export const isRegistrationClosedVisible = (event) => {
    if (isDraft(event)) {
        return new Date(getEndDate(event)) < new Date();
    }
    return isRegistrationClosed(event);
};
export const isPersonalAgendaEnabled = (event) => event?.agenda?.enabled ?? false;
export const isEventRsvpButtonVisible = (event) => !isWithoutRegistration(event) || !event.eventDisplaySettings?.hideEventDetailsButton;
export const hasSeatingPlan = (event) => Boolean(event?.registration?.ticketing?.config?.stockManagerAppId);
export const isRegistrationWarningVisible = (event) => isWithoutRegistration(event) || isRegistrationExternal(event);
export const ticketedWithTickets = (event) => ticketedOrInitiallyTicketed(event) && hasVisibleTicketDefinitions(event);
export const ticketedNoTickets = (event) => ticketedOrInitiallyTicketed(event) && !hasVisibleTicketDefinitions(event);
export const sortEvents = (eventList) => {
    const upcomingEvents = getEventsByStatusSortedByDate(eventList, [
        EventStatusNumberEnum.SCHEDULED,
        EventStatusNumberEnum.STARTED,
        AmbassadorEventStatus.SCHEDULED,
        AmbassadorEventStatus.STARTED,
    ]);
    const pastEvents = getEventsByStatusSortedByDate(eventList, [
        EventStatusNumberEnum.ENDED,
        AmbassadorEventStatus.ENDED,
    ]);
    const canceledEvents = getEventsByStatusSortedByDate(eventList, [
        EventStatusNumberEnum.CANCELED,
        AmbassadorEventStatus.CANCELED,
    ]);
    return [].concat(upcomingEvents, pastEvents, canceledEvents);
};
const getEventsByStatusSortedByDate = (eventList, statuses) => eventList.filter(event => statuses.indexOf(event.status) > -1);
//# sourceMappingURL=event.js.map